import React, { useContext, useRef, useEffect, useState } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';
import { Shield, CheckCircle, Award } from 'lucide-react';
import { TooltipContext } from './TooltipContext';
import verificationService from '../../services/verificationService';
import './VerificationBadges.css';

const VerificationBadges = ({ providerId, skipVerification = false, providerType = 'CRNA', isCertified = false, onBadgeClick }) => {
  const { openId, setOpenId } = useContext(TooltipContext);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const targetRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Skip verification loading for anonymous/mock providers or when explicitly disabled
    if (providerId && !providerId.startsWith('anon_') && !skipVerification) {
      loadVerifications();
    } else {
      setLoading(false);
    }
    return () => clearTimeout(timerRef.current);
  }, [providerId, skipVerification]);

  const loadVerifications = async () => {
    try {
      console.log('VerificationBadges: Loading for provider:', providerId);
      const response = await verificationService.getVerificationStatus(providerId);
      console.log('VerificationBadges: Response:', response);

      if (response.success && response.data) {
        // Only show verified credentials
        const verified = response.data.filter(v => v.verificationStatus === 'VERIFIED');
        setVerifications(verified);
      } else {
        setVerifications([]);
      }
    } catch (error) {
      const status = error?.response?.status ?? error?.status;
      if (status === 403) {
        // Expected, not an error: the verification endpoint refuses viewers
        // the guard does not authorize (a company browsing a pre-negotiation
        // deck, say) by design. Those surfaces should pass skipVerification,
        // but any that do not must degrade to "no badge" silently rather than
        // fill the console with a refusal that is working as intended.
        setVerifications([]);
      } else {
        console.error('Error loading verifications for badge:', error);
        setVerifications([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();

    // If a click handler is provided, use it (e.g., navigate to vault)
    if (onBadgeClick) {
      onBadgeClick();
      return;
    }

    // Otherwise show tooltip (default behavior)
    show(e);
  };

  const show = e => {
    e && e.stopPropagation();
    clearTimeout(timerRef.current);
    setOpenId('verification-badges');
    timerRef.current = setTimeout(() => {
      setOpenId(curr => curr === 'verification-badges' ? null : curr);
    }, 2000);
  };

  const hide = e => {
    e && e.stopPropagation();
    clearTimeout(timerRef.current);
    setOpenId(curr => curr === 'verification-badges' ? null : curr);
  };

  // Show badge if certified OR has verified credentials
  if (loading || (!isCertified && verifications.length === 0)) {
    return null;
  }

  const getTooltipText = () => {
    const parts = [];

    // Add certification info
    if (isCertified) {
      const certText = providerType === 'Anesthesiologist'
        ? 'Board-Certified Anesthesiologist'
        : 'Board-Certified CRNA';
      parts.push(certText);
    }

    // Add verified credentials
    if (verifications.length > 0) {
      const types = verifications.map(v =>
        v.credentialType.replace(/_/g, ' ')
      ).join(', ');
      parts.push(`Verified: ${types}`);
    }

    return parts.join(' • ');
  };

  return (
    <>
      <span
        ref={targetRef}
        className="verification-badges-container"
        onClick={handleClick}
        onTouchStart={show}
        onMouseEnter={show}
        onMouseLeave={hide}
        style={{ cursor: 'pointer' }}
      >
        <Shield size={18} className="verification-icon verified" />
        {verifications.length > 0 && (
          <span className="verification-count">{verifications.length}</span>
        )}
      </span>

      <Overlay
        target={targetRef.current}
        show={openId === 'verification-badges'}
        placement="top"
        transition={false}
        rootClose={true}
        onHide={hide}
      >
        <Tooltip id="tooltip-verification-badges">
          {getTooltipText()}
        </Tooltip>
      </Overlay>
    </>
  );
};

export default VerificationBadges;
